import React from "react";
import { Box, Grid, Card, CardContent, Typography, Stack } from "@mui/material";
import { motion } from "framer-motion";

const services = [
  {
    title: "Customer Software Solutions",
    description:
      "Custom software created specifically for your business or service will increase productivity without forcing organizational change.",
    icon: "💻",
    borderColor: "#FFCCBC",
    accentColor: "#f96639ff",
    accentType: "topLeftL",
  },
  {
    title: "Web, Cloud, SaaS",
    description:
      "Custom software created specifically for your business or service will increase productivity without forcing organizational change.",
    icon: "☁️",
    borderColor: "#BBDEFB",
    accentColor: "#46a7f6ff",
    accentType: "topCenterLongPill",
  },
  {
    title: "Amazon FBA (MWS) and eCommerce",
    description:
      "Custom software created specifically for your business or service will increase productivity without forcing organizational change.",
    icon: "🛒",
    borderColor: "#B2EBF2",
    accentColor: "#38b2c2ff",
    accentType: "topRightL",
  },
  {
    title: "Mobile Applications",
    description:
      "Custom software created specifically for your business or service will increase productivity without forcing organizational change.",
    icon: "📱",
    borderColor: "#E1BEE7",
    accentColor: "#af3bcfff",
    accentType: "bottomLeftL",
  },
  {
    title: "Database Expertise",
    description:
      "Custom software created specifically for your business or service will increase productivity without forcing organizational change.",
    icon: "🗄️",
    borderColor: "#C8E6C9",
    accentColor: "#42a146ff",
    accentType: "bottomCenterLongPill",
  },
  {
    title: "Technical Documentation",
    description:
      "Custom software created specifically for your business or service will increase productivity without forcing organizational change.",
    icon: "📄",
    borderColor: "#FFE0B2",
    accentColor: "#FB8C00",
    accentType: "bottomRightL",
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

const renderAccent = (type, color) => {
  const thickness = { xs: 4, sm: 5, md: 5.4 };
  const length = { xs: 40, sm: 45, md: 50 };
  const radius = { xs: "12px", sm: "14px", md: "14px" };

  switch (type) {
    case "topLeftL":
      return (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: { xs: 40 + 4, sm: 45 + 5, md: 48 + 4 }, // length + thickness
            height: { xs: 40 + 4, sm: 45 + 5, md: 48 + 4 }, // length + thickness
            borderTopWidth: thickness,
            borderLeftWidth: thickness,
            borderBottomWidth: 0,
            borderRightWidth: 0,
            borderTopStyle: "solid",
            borderLeftStyle: "solid",
            borderColor: color,
            borderTopLeftRadius: radius,
            backgroundColor: "transparent",
            zIndex: 2,
          }}
        />
      );
    case "topRightL":
      return (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: { xs: 40 + 4, sm: 45 + 5, md: 50 + 6 },
            height: { xs: 40 + 4, sm: 45 + 5, md: 50 + 6 },
            borderTopWidth: thickness,
            borderRightWidth: thickness,
            borderBottomWidth: 0,
            borderLeftWidth: 0,
            borderTopStyle: "solid",
            borderRightStyle: "solid",
            borderColor: color,
            borderTopRightRadius: radius,
            backgroundColor: "transparent",
            zIndex: 2,
          }}
        />
      );
    case "bottomLeftL":
      return (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: { xs: 40 + 4, sm: 45 + 5, md: 50 + 6 },
            height: { xs: 40 + 4, sm: 45 + 5, md: 50 + 6 },
            borderBottomWidth: thickness,
            borderLeftWidth: thickness,
            borderTopWidth: 0,
            borderRightWidth: 0,
            borderBottomStyle: "solid",
            borderLeftStyle: "solid",
            borderColor: color,
            borderBottomLeftRadius: radius,
            backgroundColor: "transparent",
            zIndex: 2,
          }}
        />
      );
    case "bottomRightL":
      return (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
            width: { xs: 40 + 4, sm: 45 + 5, md: 50 + 6 },
            height: { xs: 40 + 4, sm: 45 + 5, md: 50 + 6 },
            borderBottomWidth: thickness,
            borderRightWidth: thickness,
            borderTopWidth: 0,
            borderLeftWidth: 0,
            borderBottomStyle: "solid",
            borderRightStyle: "solid",
            borderColor: color,
            borderBottomRightRadius: radius,
            backgroundColor: "transparent",
            zIndex: 2,
          }}
        />
      );
    case "topCenterLongPill":
      return (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "calc(50% - 25px)",
            width: { xs: 50, sm: 55, md: 60 },
            height: thickness,
            backgroundColor: color,
            borderRadius: radius,
            zIndex: 2,
          }}
        />
      );
    case "bottomCenterLongPill":
      return (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: "calc(50% - 25px)",
            width: { xs: 50, sm: 55, md: 60 },
            height: thickness,
            backgroundColor: color,
            borderRadius: radius,
            zIndex: 2,
          }}
        />
      );
    default:
      return null;
  }
};

const OurService = () => {
  return (
    <Box
      component="section"
      aria-label="Our Services - Vertex Suite"
      sx={{
        maxWidth: { xs: "100%", sm: "90%", md: "1280px" },
        mx: "auto",
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
        py: { xs: 2, sm: 3, md: 2, lg: 2 },
        borderRadius: "12px",
        backdropFilter: { xs: "none", sm: "blur(6px)" },
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="stretch">
        {services.map((service, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <motion.div
              custom={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              style={{ height: "100%" }}
            >
              <Card
                component="article"
                sx={{
                  border: `2px solid ${service.borderColor}`,
                  borderRadius: "16px",
                  overflow: "hidden",
                  position: "relative",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                  },
                }}
              >
                {renderAccent(service.accentType, service.accentColor)}
                <CardContent sx={{ flexGrow: 1, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                  <Stack
                    spacing={1.4}
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    height="100%"
                  >
                    <Typography fontSize={{ xs: "30px", sm: "34px", md: "36px" }} lineHeight={1}>
                      {service.icon}
                    </Typography>
                    <Typography
                      variant="h6"
                      component="h3"
                      fontWeight={600}
                      sx={{
                        // border: "1px solid red",
                        color: service.accentColor,
                        fontFamily: "'Roboto', sans-serif",
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        lineHeight: 1.3,
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        // border: "1px solid red",
                        fontSize: { xs: "15px", sm: "17px", md: "17px" },
                        fontWeight: "400",
                        fontFamily: "'Roboto', sans-serif",
                        // color: "#333",
                        lineHeight: { xs: 1.5, sm: 1.5, md: 1.3 },
                        px: 1,
                        mb: { xs: 2, sm: 2, md: 1.5, lg: 1.5 },
                      }}
                    >
                      {service.description}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default OurService;